/** Source type discriminant for a knowledge asset card, matching the backend enum. */
export type AssetCardSourceType = 'note' | 'document' | 'file_url' | 'markdown_import';

/** Mutable form state for the asset editor — fields may be empty or raw from user input. */
export interface AssetEditorDraft {
  title: string;
  markdown: string;
  tags: string[];
}

/** Normalise line endings and trim surrounding whitespace for consistent comparison. */
const normalizeText = (value: string): string => value.replaceAll('\r\n', '\n').trim();

/** Deduplicate, trim, filter empty, and sort tags alphabetically. */
const normalizeTags = (tags: readonly string[]): string[] =>
  [...new Set(tags.map((tag) => tag.trim()).filter(Boolean))].toSorted((left, right) =>
    left.localeCompare(right),
  );

/**
 * Immutable, normalised snapshot of the asset editor form used for dirty-state detection.
 *
 * Constructed from an AssetEditorDraft at save-point or on load; compare a later draft
 * via `hasChanges` to determine whether the form has unsaved edits.
 */
export class AssetEditorSnapshot {
  readonly title: string;
  readonly markdown: string;
  readonly tags: readonly string[];

  constructor(draft: Readonly<AssetEditorDraft>) {
    this.title = normalizeText(draft.title);
    this.markdown = normalizeText(draft.markdown);
    this.tags = Object.freeze(normalizeTags(draft.tags));
  }

  /**
   * Return true when the current draft differs from this snapshot after normalisation.
   *
   * @param draft - The live form state to compare against this snapshot.
   */
  hasChanges(draft: Readonly<AssetEditorDraft>): boolean {
    const current = new AssetEditorSnapshot(draft);
    return (
      current.title !== this.title ||
      current.markdown !== this.markdown ||
      current.tags.length !== this.tags.length ||
      current.tags.some((tag, index) => tag !== this.tags[index])
    );
  }

  /**
   * Return a new snapshot with the markdown field replaced, preserving title and tags.
   *
   * @param markdown - The updated Markdown content from the editor.
   */
  withMarkdown(markdown: string): AssetEditorSnapshot {
    return new AssetEditorSnapshot({ title: this.title, markdown, tags: [...this.tags] });
  }
}
